import { ApiClient } from '../ApiClient';

export class UserApi {
  /**
   * Constructs a new UserApi.
   * @alias module:api/UserApi
   * @class
   * @param {module:ApiClient} [apiClient] Optional API client implementation to use,
   * default to {@link module:ApiClient#instance} if unspecified.
   */
  constructor(apiClient) {
    this.apiClient = apiClient || ApiClient.userApiInstance;
  }

  /**
   * Get user data from address
   *
   * @param {string} address
   */
  async getUser(address) {
    return await this.apiClient.callApi('/user/{address}', 'get', { address }, {}, null);
  }

  /**
   * Update user data
   *
   * @param {{*}} userData The user date to update to server
   */
  async updateUser(userData) {
    return await this.apiClient.callApi('/user/update', 'post', {}, {}, null, undefined, userData);
  }

  /**
   * Get nonce preparing for authentication
   *
   * @param {string} address
   */
  async getNonce(address) {
    return await this.apiClient.callApi('/user/nonce/{address}', 'get', { address }, {}, null);
  }

  /**
   * Authenticate user
   *
   * @param {{address, signature}} body User address is stored here
   */
  async authenticate(body) {
    return await this.apiClient.callApi('/user/authenticate', 'post', {}, {}, null, {}, body);
  }

  /**
   * Log user out
   *
   */
  async logout() {
    return await this.apiClient.callApi('/user/logout', 'post', {}, {}, null);
  }

  /**
   * Add new favorite collection for current logged user
   * @param {{tokenAddress, tokenId}} body
   */
  async favoriteCollection(body) {
    return await this.apiClient.callApi(
      '/favorite/collection',
      'post',
      {},
      {},
      null,
      undefined,
      body,
    );
  }

  /**
   * Remove a favorite collection for current logged user
   * @param {{tokenAddress, tokenId}} body
   */
  async unfavoriteCollection(body) {
    return await this.apiClient.callApi(
      '/favorite/collection',
      'delete',
      {},
      {},
      null,
      undefined,
      body,
    );
  }

  /**
   * Add new favorite LP Bond collection for current logged user
   * @param {{tokenAddress: string, chain: string}} body LP Bond collection contract address and chain
   */
  async favoriteLPBondCollection(body) {
    return await this.apiClient.callApi(
      '/favorite/lpbond/collection',
      'post',
      {},
      {},
      null,
      undefined,
      body,
    );
  }

  /**
   * Remove a favorite LP Bond collection for current logged user
   * @param {{tokenAddress: string, chain: string}} body LP Bond collection contract address and chain
   */
  async unfavoriteLPBondCollection(body) {
    return await this.apiClient.callApi(
      '/favorite/lpbond/collection',
      'delete',
      {},
      {},
      null,
      undefined,
      body,
    );
  }

  /**
   * Get all received offers from user
   * @param {String} address Address from user
   */
  async getReceivedOffers({ address }) {
    return await this.apiClient.callApi('/user/offersReceived', 'get', {}, { address }, null);
  }

  /**
   * Lock account
   */
  async lockAccount() {
    return await this.apiClient.callApi('/user/lock', 'post', {}, {}, null);
  }

  async updateCollectionImage({ data }) {
    return await this.apiClient.callApi(
      '/collection/updateimage',
      'post',
      {},
      {},
      null,
      undefined,
      data,
    );
  }

  async getPortfolioStats({ collection, userAddress }) {
    return await this.apiClient.callApi(
      '/user/lpBondPortfolioStats',
      'get',
      {},
      { collection, userAddress },
      null,
    );
  }

  async applyReferral(body) {
    return await this.apiClient.callApi('/user/apply-referral', 'post', {}, {}, null, {}, body);
  }

  async getReferralReward() {
    return await this.apiClient.callApi('/user/referral-reward', 'get', {}, {}, null);
  }

  async getOwnedBids(address) {
    return await this.apiClient.callApi(
      '/collection/ownedBids/{address}',
      'get',
      { address },
      {},
      null,
    );
  }

  async getTotalPlacedBids(address) {
    return await this.apiClient.callApi(
      '/collection/totalPlacedBids/{address}',
      'get',
      { address },
      {},
      null,
    );
  }

  async updateSocials({ address, body }) {
    return await this.apiClient.callApi(
      '/collection/updateSocials/{address}',
      'post',
      { address },
      {},
      null,
      {},
      body,
    );
  }
}
